import { useFormContext, useWatch } from "react-hook-form";

import { ResourceFormActions } from "@/shared/components";
import { type SubmitError } from "@/shared/components/ResourceForm";

import { submitKindFor } from "../formScope";
import { type TriggerFormValues } from "../triggerFormSchema";

type SubmitKind = ReturnType<typeof submitKindFor>;

/** The words for each submit the form can show. */
const SUBMIT_LABELS: Record<SubmitKind, string> = {
  create: "Create",
  "create-and-enable": "Create and enable",
  save: "Save changes",
};

/** What is left to do, said in terms of the submit the row is offering. */
const NOTHING_SUBMITTED_YET: Record<SubmitKind, string> = {
  create: "Fill in the sections above, then create the trigger.",
  "create-and-enable": "Fill in the sections above, then create the trigger.",
  save: "Change what you need above, then save the trigger.",
};

export interface FormActionsProps {
  mode: "create" | "edit";
  onCancel?: () => void;
  /** Why the last submission failed, shown with the submit it answers. */
  serverError: SubmitError | null;
}

/**
 * The shared actions row, told which submit this form is offering: what a
 * trigger's submit says turns on whether it will be enabled on create, which
 * is the one thing about the row that is the trigger form's own.
 */
export function FormActions({ mode, onCancel, serverError }: FormActionsProps) {
  const { control } = useFormContext<TriggerFormValues>();
  const enabled = useWatch({ control, name: "enabled" });
  const submitKind = submitKindFor(mode, enabled);

  return (
    <ResourceFormActions
      submitLabel={SUBMIT_LABELS[submitKind]}
      idleHint={NOTHING_SUBMITTED_YET[submitKind]}
      onCancel={onCancel}
      submitKind={submitKind}
      serverError={serverError}
    />
  );
}
